import { Box, Chip, Stack } from "@mui/material";
import { Custom } from "components";
import { useApp, useTheme } from "contexts";
import { ICategory, IMenu, IUser } from "interfaces";
import { useEffect, useState } from "react";

// icons
import {
  AddRounded,
  FavoriteRounded,
  PublishedWithChangesRounded,
} from "@mui/icons-material";

type Props = {
  open: boolean;
  menu: IMenu;
  category: ICategory;
  participants: IUser[];
  onCancel: (id: string) => void;
  onUpdate: (id: string, category: ICategory) => void;
  onClose: () => void;
};

export const ManageItem = ({
  open,
  menu,
  category,
  participants,
  onUpdate,
  onCancel,
  onClose,
}: Props) => {
  const { t } = useApp();
  const { theme } = useTheme();

  const [categoryName, setCategoryName] = useState<string>("");
  const [currentCategory, setCurrentCategory] = useState<ICategory>(category);

  useEffect(() => {
    setCurrentCategory(category);
  }, [category]);

  useEffect(() => {
    if (currentCategory) setCategoryName(currentCategory.replaced_with || "");
  }, [currentCategory]);

  const isAssignedTo = (id: string) =>
    currentCategory?.assigned_to.some((item) => item._id === id);

  const handleAdd = (i: number) => {
    setCurrentCategory((prev) => {
      const updatedCategory = {
        ...prev,
        assigned_to: [...prev.assigned_to, participants[i]],
      };
      return updatedCategory;
    });
  };

  const handleReplace = (i: number) => {
    setCurrentCategory((prev) => {
      const updatedCategory = {
        ...prev,
        assigned_to: [participants[i]],
      };
      return updatedCategory;
    });
  };

  const handleRemove = (id: string) => {
    setCurrentCategory((prev) => {
      const updatedCategory = {
        ...prev,
        assigned_to: prev.assigned_to.filter((item) => item._id !== id),
      };
      return updatedCategory;
    });
  };

  const handleOnClose = () => {
    setCurrentCategory((prev) => {
      const updatedCategory = { ...prev, replaced_with: categoryName };
      onUpdate(category._id, updatedCategory);
      return updatedCategory;
    });
    onClose();
  };

  return (
    currentCategory && (
      <Custom.Modal open={open} onClose={onClose} title={currentCategory.name}>
        <Box>
          <Stack spacing={theme.spacing.md}>
            <Custom.Typography
              align="center"
              size={theme.font.xs}
              weight={theme.font.bold}
            >
              {menu.configuration.name} |{" "}
              {`${currentCategory.suggested_by.first_name} ${currentCategory.suggested_by.last_name}`}
            </Custom.Typography>
          </Stack>
        </Box>

        <Box mt={theme.spacing.md}>
          <Stack spacing={theme.spacing.xs}>
            <Custom.Typography size={theme.font.xs} weight={theme.font.bold}>
              {t.component.popup.manage_item.reassign_member}
            </Custom.Typography>
            <Custom.Typography size={theme.font.xs} weight={theme.font.normal}>
              {t.component.popup.manage_item.replace_add}
            </Custom.Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            border: 1,
            maxHeight: 300,
            overflowY: "auto",
            mt: theme.spacing.md,
            borderRadius: theme.border.radius,
            borderColor: theme.color.border.color,
          }}
        >
          <Stack>
            {participants.map((participant: IUser, i: number) => (
              <Box
                key={i}
                sx={
                  i === 0
                    ? {}
                    : { borderTop: 1, borderColor: theme.color.border.color }
                }
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  px={theme.spacing.sm}
                  py={theme.spacing.xs}
                  justifyContent="space-between"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={theme.spacing.sm}
                  >
                    <Custom.Avatar
                      src={participant._id}
                      alt={participant.first_name}
                    />
                    <Custom.Typography
                      size={theme.font.sm}
                      weight={theme.font.medium}
                    >
                      {`${participant.first_name} ${participant.last_name}`}
                    </Custom.Typography>
                  </Stack>
                  {!isAssignedTo(participant._id) && (
                    <Stack direction="row" spacing={theme.spacing.xs}>
                      <Custom.Button
                        size="small"
                        sx={{ fontSize: theme.font.xxs }}
                        endIcon={<AddRounded />}
                        onClick={() => handleAdd(i)}
                      >
                        {t.action.add}
                      </Custom.Button>

                      <Custom.Button
                        size="small"
                        disabled={currentCategory.assigned_to.length !== 1}
                        sx={{ fontSize: theme.font.xxs }}
                        endIcon={<PublishedWithChangesRounded />}
                        onClick={() => handleReplace(i)}
                      >
                        {t.action.replace}
                      </Custom.Button>
                    </Stack>
                  )}
                  {isAssignedTo(participant._id) && (
                    <Box py={theme.spacing.sm}>
                      <Chip
                        size="small"
                        icon={
                          <FavoriteRounded
                            sx={{
                              width: 16,
                              height: 16,
                              fill: theme.color.status.error.color,
                            }}
                          />
                        }
                        label={t.label.assigned}
                        onDelete={() => handleRemove(participant._id)}
                      />
                    </Box>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box mt={theme.spacing.md} width={1}>
          <Custom.Typography size={theme.font.xs} weight={theme.font.bold}>
            {t.component.popup.manage_item.action_item}
          </Custom.Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={theme.spacing.md}
            justifyContent="space-between"
          >
            <Custom.Typography size={theme.font.sm} weight={theme.font.normal}>
              {t.action.modify}
            </Custom.Typography>
            <Custom.Button
              size="small"
              trailing="delete"
              onClick={() => onCancel(currentCategory._id)}
            >
              {t.action.cancel}
            </Custom.Button>
          </Stack>
        </Box>

        <Box mt={theme.spacing.xs}>
          <Custom.TextField
            fullWidth
            label={t.label.replace_with}
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
        </Box>

        <Custom.Button
          fullWidth
          trailing="right"
          variant="contained"
          sx={{ mt: theme.spacing.md }}
          onClick={handleOnClose}
        >
          {t.action.save}
        </Custom.Button>
      </Custom.Modal>
    )
  );
};
